import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { assertAdmin } from "@/utils/adminAuth.server";

const sourceSchema = z.object({
  sourcePath: z.string().min(2).max(500).startsWith("/"),
  locale: z.string().max(8).nullable(),
});

const reviewSchema = z.object({
  id: z.string().uuid(),
  sourcePath: z.string().min(2).max(500).startsWith("/"),
  destinationPath: z
    .string()
    .max(500)
    .optional(),
  status: z.enum(["approved", "ignored"]),
}).superRefine((value, ctx) => {
  if (value.status !== "approved") return;
  if (!value.destinationPath?.startsWith("/") || value.destinationPath.startsWith("//")) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["destinationPath"],
      message: "Destination must be a local path",
    });
  }
});

export type LegacyUrlRegistryRow = {
  id: string;
  source_path: string;
  locale: string | null;
  visit_count: number;
  first_seen_at: string;
  last_seen_at: string;
  status: "pending" | "approved" | "ignored";
  destination_path: string | null;
  reviewed_at: string | null;
};

/** Records only a normalized path. No query, referrer, IP, user agent, or PII. */
export const recordLegacyUrlVisit = createServerFn({ method: "POST" })
  .inputValidator((input) => sourceSchema.parse(input))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: destination, error } = await supabaseAdmin.rpc(
      "record_legacy_url_visit",
      { _source_path: data.sourcePath, _locale: data.locale ?? undefined },
    );
    if (error) return { destinationPath: null };
    return {
      destinationPath: typeof destination === "string" ? destination : null,
    };
  });

export const listLegacyUrlsAdmin = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin
      .from("legacy_url_registry")
      .select("id,source_path,locale,visit_count,first_seen_at,last_seen_at,status,destination_path,reviewed_at")
      .order("visit_count", { ascending: false })
      .order("last_seen_at", { ascending: false })
      .limit(500);
    if (error) throw new Error(error.message);
    return (data ?? []) as LegacyUrlRegistryRow[];
  });

export const reviewLegacyUrlAdmin = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => reviewSchema.parse(input))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const sourceBase = data.sourcePath.split("?")[0]?.replace(/\/+$/, "") || "/";
    const destinationPath = data.destinationPath ?? "";
    const destinationBase = destinationPath.split("?")[0]?.replace(/\/+$/, "") || "/";
    if (data.status === "approved" && sourceBase === destinationBase) {
      throw new Error("Redirect loops are not allowed");
    }

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const destination = data.status === "approved" ? destinationPath : null;
    const { error } = await supabaseAdmin
      .from("legacy_url_registry")
      .update({
        status: data.status,
        destination_path: destination,
        reviewed_by: context.userId,
        reviewed_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq("id", data.id)
      .eq("source_path", data.sourcePath);
    if (error) throw new Error(error.message);

    await supabaseAdmin.from("admin_audit_log").insert({
      actor_user_id: context.userId,
      action: data.status === "approved" ? "legacy_redirect_mapped" : "legacy_redirect_ignored",
      target_type: "legacy_url",
      target_key: data.sourcePath,
      after_state: { destination_path: destination, status: data.status },
    });

    return { ok: true };
  });